/**
 * <HowItWorks /> — the /how-it-works page, and the HowTo JSON-LD that goes
 * with it.
 *
 * ONE SOURCE, TWO OUTPUTS
 *   The visible steps and the structured data are both built from
 *   src/content/how-it-works.js, in the same render, exactly as /faq is.
 *   Never paste a step into buildHowToJsonLd().
 *
 * WHY IT IS SHAPED THIS WAY
 *   <h1> comes from <Page>. The steps are a real <ol>, one <h2> per step,
 *   and the step's words sit in one element marked data-step-text, which
 *   is what scripts/check-how-it-works.js compares with the JSON-LD. A
 *   footnote marker sits outside that element, so the marker never leaks
 *   into the structured data. The footnotes are smaller type below the
 *   steps, and each marker links to its note.
 *
 *   Google retired HowTo rich results in 2023. The JSON-LD is still worth
 *   emitting: it is an exact step map for answer engines. Validate it on
 *   validator.schema.org, not the Rich Results Test.
 *
 * See docs/ledgers/aeo/AEO-LEDGER.md, task 11b.
 */

import styled from 'styled-components';

import Page from '../Page';
import Seo from '../Seo';
import Footer from '../Shared/NewFooter';
import Header from '../Shared/NewHeader';
import howItWorks, { stepText } from '../../content/how-it-works';

import { colors, ContentWrapper, mobile, PageWrapper, tablet } from '../styles';

const PAGE_URL = 'https://yedding.com/how-it-works';

/**
 * The HowTo node, built from the same data the page renders.
 *
 * Exported so scripts/check-how-it-works.js can be pointed at it when a
 * check needs proving: change one word here and the check must turn red.
 */
export const buildHowToJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: howItWorks.howToName,
  description: howItWorks.intro,
  step: howItWorks.steps.map((step, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: step.name,
    text: stepText(step),
    url: `${PAGE_URL}#${step.id}`
  }))
});

const footnoteFor = (id) => howItWorks.footnotes.find((note) => note.id === id);

// The same frame and type scale as src/components/Faq, so the content pages
// read alike. Improvement note from task 10: these rules are now written
// three times, and want one shared component.
const Wrapper = styled(ContentWrapper)`
  & > main {
    max-width: 908px;
    margin: 0 auto;
    padding: 40px 44px 60px;

    @media (max-width: ${tablet}) {
      padding: 32px 24px 48px;
    }

    @media (max-width: ${mobile}) {
      padding: 24px 15px 40px;
    }
  }
`;

const Article = styled.div`
  color: ${colors.defaultText};
  font-size: 16px;
  line-height: 1.65;

  @media (max-width: ${mobile}) {
    font-size: 15px;
  }

  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 36px 0 12px;
    // Clears the fixed header when /how-it-works#event-insurance opens.
    scroll-margin-top: 130px;
  }

  p {
    margin: 0 0 14px;
  }

  ul {
    margin: 0 0 14px;
    padding-left: 22px;
  }

  li {
    margin: 0 0 8px;
  }
`;

const Intro = styled.p`
  color: ${colors.fadedText};
  margin: 0 0 8px !important;
`;

// The numbers are in the <h2> text, so the list draws no markers of its own.
const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Marker = styled.a`
  &,
  &:visited {
    color: ${colors.fadedText};
  }
  text-decoration: none;
  margin-left: 2px;
`;

// The marker is the only thing added to a step's words. It carries
// data-footnote-marker so the check can lift it out before comparing the
// visible text with the JSON-LD, which never contains it.
const NoteMarker = ({ note }) => (
  <Marker href={`#note-${note.id}`} aria-label={`Note: ${note.label}`} data-footnote-marker>
    <sup>{note.marker}</sup>
  </Marker>
);

const Notes = styled.section`
  border-top: 1px solid #e0e0e0;
  color: ${colors.fadedText};
  font-size: 14px;
  line-height: 1.6;
  margin-top: 40px;
  padding-top: 20px;

  p {
    margin: 0 0 12px;
    scroll-margin-top: 130px;
  }
`;

const HowItWorks = () => (
  <PageWrapper>
    <Seo jsonLd={buildHowToJsonLd()} />
    <Header omitSearch />
    <Wrapper>
      <Page title={howItWorks.title}>
        <Article>
          <Intro>{howItWorks.intro}</Intro>

          <Steps>
            {howItWorks.steps.map((step, index) => {
              const note = step.footnote && footnoteFor(step.footnote);
              return (
                <li key={step.id}>
                  <h2 id={step.id}>
                    {index + 1}. {step.name}
                  </h2>
                  <div data-step-text>
                    {step.paragraphs.map((text, i) => (
                      <p key={text}>
                        {text}
                        {note && !step.list && i === step.paragraphs.length - 1 && <NoteMarker note={note} />}
                      </p>
                    ))}
                    {step.list && (
                      <ul>
                        {step.list.map((item) => (
                          <li key={item.label}>
                            <strong>{item.label}</strong> {item.text}
                          </li>
                        ))}
                      </ul>
                    )}
                    {(step.closing || []).map((text) => (
                      <p key={text}>{text}</p>
                    ))}
                  </div>
                </li>
              );
            })}
          </Steps>

          <Notes aria-label="Notes">
            {howItWorks.footnotes.map((note) => (
              <p key={note.id} id={`note-${note.id}`}>
                {note.marker} <strong>{note.label}</strong> {note.text}
              </p>
            ))}
          </Notes>
        </Article>
      </Page>
    </Wrapper>
    <Footer />
  </PageWrapper>
);

export default HowItWorks;
